// Generator detail card for SLD detail panel — Gamma design system
import { StatusBadge } from './shared/StatusBadge';
import { ProgressBar } from './shared/ProgressBar';
import { DeviceConditionSections } from './shared/DeviceConditionSections';
import { formatVoltage } from '../../../utils/formatVoltage';

interface GeneratorDetailCardProps {
  siteId?: string;
  data: Record<string, unknown>;
  onViewDetails: () => void;
}

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

function fmt(v: unknown, decimals = 1): string {
  if (v == null) return '--';
  const n = Number(v);
  if (!isFinite(n)) return '--';
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function GeneratorDetailCard({ siteId, data, onViewDetails }: GeneratorDetailCardProps) {
  const status = String(data.status ?? data.state ?? data.blockStatus ?? 'unknown').toLowerCase();
  const label = String(data.label ?? 'Generator');
  const deviceId = data.deviceId as string | undefined;
  const ratedPower = num(data.ratedPowerKw);
  const faulted = data.faulted === true || data.faulted === 'true';

  // Power metrics
  const activePower = num(data.activePowerKw ?? data.generatorPowerKw);
  const reactivePower = num(data.reactivePowerKvar);
  const apparentPower = num(data.apparentPowerKva);
  const powerFactor = num(data.powerFactor);

  // Per-phase electrical
  const voltageA = num(data.voltagePhaseA);
  const voltageB = num(data.voltagePhaseB);
  const voltageC = num(data.voltagePhaseC);
  const currentA = num(data.currentPhaseA);
  const currentB = num(data.currentPhaseB);
  const currentC = num(data.currentPhaseC);
  const avgVoltage = num(data.avgLineVoltageV ?? data.voltageV);
  const frequency = num(data.frequencyHz ?? data.slipFrequencyHz);

  // Engine metrics
  const engineLoad = num(data.engineLoadPct);
  const engineSpeed = num(data.engineSpeedRpm);
  const fuelLevel = num(data.fuelLevelPct);
  const coolantTemp = num(data.coolantTempF);
  const oilPressure = num(data.oilPressurePsi);
  const batteryVoltage = num(data.batteryVoltageV);

  // Breakers
  const breakerClosed = data.breakerClosed;
  const estopLatched = data.estopLatched;

  // Status display
  const displayStatus = faulted ? 'fault' : status;
  const statusLabel = displayStatus === 'on' || displayStatus === 'running' ? 'ON' : displayStatus.toUpperCase();

  // Determine if we have per-phase data
  const hasPerPhase = (voltageA != null || currentA != null);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-ems-border flex-shrink-0">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-ems-text-primary truncate">{label}</h3>
          <StatusBadge status={statusLabel} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-ems-text-muted">Generator</span>
          {ratedPower != null && (
            <>
              <span className="text-ems-text-muted opacity-40">&middot;</span>
              <span className="text-xs text-ems-text-muted font-mono">{ratedPower.toLocaleString()} kW</span>
            </>
          )}
        </div>
        {deviceId && (
          <span className="text-[10px] text-ems-text-muted font-mono mt-0.5 block opacity-60">{deviceId}</span>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {/* Power Section — Primary */}
        <div>
          <span className="text-xs uppercase tracking-wider text-ems-text-secondary mb-2 block">Power</span>
          <div className="grid grid-cols-3 gap-2">
            <PowerMetricCard label="kW" value={activePower} />
            <PowerMetricCard label="kVAR" value={reactivePower} />
            <PowerMetricCard label="kVA" value={apparentPower} />
          </div>
          {powerFactor != null && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-ems-bg px-3 py-1.5">
              <span className="text-[10px] text-ems-text-muted uppercase tracking-wide">PF</span>
              <span className="text-sm font-mono font-semibold text-ems-text-primary transition-all duration-300">
                {powerFactor.toFixed(3)}
              </span>
            </div>
          )}
        </div>

        {/* Electrical Section — Per-phase table or average */}
        <div>
          <span className="text-xs uppercase tracking-wider text-ems-text-secondary mb-1.5 block">Electrical</span>
          {hasPerPhase ? (
            <div className="rounded-xl bg-ems-bg overflow-hidden">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-ems-text-muted uppercase tracking-wide">
                    <th className="text-left px-3 pt-2 pb-1 font-medium">Phase</th>
                    <th className="text-right px-3 pt-2 pb-1 font-medium">Voltage</th>
                    <th className="text-right px-3 pt-2 pb-1 font-medium">Current</th>
                  </tr>
                </thead>
                <tbody className="text-ems-text-primary">
                  <PhaseRow label="L1" voltage={voltageA} current={currentA} />
                  <PhaseRow label="L2" voltage={voltageB} current={currentB} />
                  <PhaseRow label="L3" voltage={voltageC} current={currentC} />
                </tbody>
              </table>
              {frequency != null && (
                <div className="px-3 py-1.5 border-t border-ems-border/50 flex items-center justify-between">
                  <span className="text-[10px] text-ems-text-muted uppercase tracking-wide">Freq</span>
                  <span className="text-xs font-mono font-semibold text-ems-text-primary">
                    {fmt(frequency, 2)} <span className="text-[10px] font-normal text-ems-text-muted">Hz</span>
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl bg-ems-bg p-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] text-ems-text-muted uppercase tracking-wide block">Voltage</span>
                  <span className="text-sm font-mono font-semibold text-ems-text-primary transition-all duration-300">
                    {formatVoltage(avgVoltage, 'ac')}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-ems-text-muted uppercase tracking-wide block">Frequency</span>
                  <span className="text-sm font-mono font-semibold text-ems-text-primary transition-all duration-300">
                    {fmt(frequency)} <span className="text-[10px] font-normal text-ems-text-muted">Hz</span>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Engine Section — Compact */}
        <div>
          <span className="text-xs uppercase tracking-wider text-ems-text-secondary mb-1.5 block">Engine</span>
          <div className="rounded-xl bg-ems-bg p-3 space-y-2">
            {/* Engine Load bar */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-ems-text-muted uppercase tracking-wide">Load</span>
                <span className="text-xs font-mono font-semibold text-ems-text-primary">
                  {engineLoad != null ? `${engineLoad.toFixed(0)}%` : '--'}
                </span>
              </div>
              <ProgressBar value={engineLoad} colorScheme="standard" showLabel={false} height="h-1" />
            </div>
            {/* Compact metrics row */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <CompactMetric label="RPM" value={engineSpeed} />
              <CompactMetric label="Fuel" value={fuelLevel} unit="%" />
              <CompactMetric label="Battery" value={batteryVoltage} unit="V" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-center">
              <CompactMetric label="Coolant" value={coolantTemp} unit={'\u00B0F'} />
              <CompactMetric label="Oil" value={oilPressure} unit="PSI" />
            </div>
          </div>
        </div>

        {/* Breakers Section */}
        {(breakerClosed != null || estopLatched != null) && (
          <div>
            <span className="text-xs uppercase tracking-wider text-ems-text-secondary mb-1.5 block">Breakers</span>
            <div className="rounded-xl bg-ems-bg p-3 grid grid-cols-2 gap-3">
              {breakerClosed != null && (
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${breakerClosed === true || breakerClosed === 'true' ? 'bg-ems-green' : 'bg-ems-red'}`} />
                  <div>
                    <span className="text-[10px] text-ems-text-muted uppercase tracking-wide block">Gen Breaker</span>
                    <span className="text-xs font-mono font-semibold text-ems-text-primary">
                      {breakerClosed === true || breakerClosed === 'true' ? 'CLOSED' : 'OPEN'}
                    </span>
                  </div>
                </div>
              )}
              {estopLatched != null && (
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${estopLatched === true || estopLatched === 'true' ? 'bg-ems-red animate-ems-pulse' : 'bg-ems-green'}`} />
                  <div>
                    <span className="text-[10px] text-ems-text-muted uppercase tracking-wide block">E-Stop</span>
                    <span className="text-xs font-mono font-semibold text-ems-text-primary">
                      {estopLatched === true || estopLatched === 'true' ? 'LATCHED' : 'CLEAR'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DeviceConditionSections
          siteId={siteId}
          deviceId={deviceId}
          data={data}
          showDtc
        />
      </div>

      {/* View Details Button */}
      <div className="px-4 py-3 border-t border-ems-border flex-shrink-0">
        <button
          onClick={onViewDetails}
          className="w-full py-2 px-4 bg-ems-forest border border-[rgba(52,211,153,0.2)] rounded-lg text-sm text-ems-green font-medium transition-colors hover:bg-ems-forest/80 hover:border-ems-border-bright"
        >
          View Details &rarr;
        </button>
      </div>
    </div>
  );
}

/** Power metric card — large centered value with unit label */
function PowerMetricCard({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="rounded-xl bg-ems-bg p-2.5 flex flex-col items-center justify-center min-h-[56px]">
      <span className="text-lg font-mono font-bold text-ems-text-primary transition-all duration-300">
        {value != null ? fmt(value) : '--'}
      </span>
      <span className="text-[10px] text-ems-text-muted uppercase tracking-wide">{label}</span>
    </div>
  );
}

/** Phase row for per-phase electrical table */
function PhaseRow({ label, voltage, current }: { label: string; voltage: number | null; current: number | null }) {
  return (
    <tr className="border-t border-ems-border/30">
      <td className="px-3 py-1.5 text-ems-text-secondary font-medium">{label}</td>
      <td className="px-3 py-1.5 text-right text-ems-text-primary">
        {voltage != null ? formatVoltage(voltage, 'ac') : '--'}
      </td>
      <td className="px-3 py-1.5 text-right text-ems-text-primary">
        {current != null ? `${fmt(current)} A` : '--'}
      </td>
    </tr>
  );
}

/** Compact metric for engine section */
function CompactMetric({ label, value, unit }: { label: string; value: number | null; unit?: string }) {
  return (
    <div>
      <span className="text-[10px] text-ems-text-muted uppercase tracking-wide block">{label}</span>
      <span className="text-xs font-mono font-semibold text-ems-text-primary transition-all duration-300">
        {value != null ? fmt(value) : '--'}
        {unit && value != null && <span className="text-[10px] font-normal text-ems-text-muted ml-0.5">{unit}</span>}
      </span>
    </div>
  );
}
